import React, { useState } from "react";
import { useParams } from "react-router-dom";
import "./AlternativeOfferModal.css";
import CloseIcon from "../../../../src/icons/X.svg";
import { ProductCard } from "./ProductCard";
import Arrow from "./Arrow/Arrow";
import * as http from "../../../utils/http";
import * as translator from "../../../utils/translator";
import { useDragModal } from "../../../customHooks/useDragModal";
import { MainTracker } from "../../../PixelTrackers/MainTracker";
import { DOCTOR_ALTERNATIVE_EVENTS, buildOrderBundleProps } from "./doctorAlternativeTracking";

export type DoctorAlternativeSuggestion = {
    originalProductTitle: string;
    originalProductPrice: number;
    originalImageSrc: string;
    originalCatalogPage: string | null;
    originalThc?: number | null;
    originalCbd?: number | null;
    originalStrain?: string | null;
    suggestedProductTitle: string;
    suggestedProductPrice: number;
    suggestedImageSrc: string;
    suggestedCatalogPage: string | null;
    suggestedThc?: number | null;
    suggestedCbd?: number | null;
    suggestedStrain?: string | null;
};

export type DoctorAlternativeOrderBundle = {
    hashID?: string;
    groupHashID?: string;
    orderStatus?: string;
    categoryShortcut?: string;
};

interface DoctorAlternativeOfferModalProps {
    closeModal: () => void;
    orderProducts: { order_id: number }[];
    doctorAlternativeSuggestions: DoctorAlternativeSuggestion[];
    orderBundle?: DoctorAlternativeOrderBundle;
    setRefreshData: (cb: (prev: boolean) => boolean) => void;
    setStickyNotificationDetails?: (details: any) => void;
}

const DoctorAlternativeOfferModal: React.FC<DoctorAlternativeOfferModalProps> = ({
    closeModal,
    orderProducts,
    doctorAlternativeSuggestions,
    orderBundle,
    setRefreshData,
    setStickyNotificationDetails
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { country } = useParams() as { country: string };
    const suggestion = doctorAlternativeSuggestions?.[0];

    const {
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        currentTranslateY,
        modalDragTransition,
        isClosing,
        closeModal: closeModalWithAnimation
    } = useDragModal({
        onClose: closeModal,
        shouldShow: true,
        preventBodyVerticalScroll: true
    });

    const submitDecision = (decision: "accept" | "deny") => {
        const orderID = orderProducts[0].order_id;
        setIsSubmitting(true);
        const queryParams = new URLSearchParams(window.location.search);
        const utmSource = queryParams.get('utm_source');
        const utmMedium = queryParams.get('utm_medium');
        let entryMethod = 'My Account direct';
        if (utmMedium === 'doctor_alternative') {
            if (utmSource === 'email') entryMethod = 'Email CTA';
            else if (utmSource === 'whatsapp') entryMethod = 'WhatsApp CTA';
        }
        http.postToServer(
            `account/doctor-alternative-decision/${orderID}`,
            { decision, entryMethod },
            [http.MIDDLEWARES.CUSTOMER_ID],
        ).then(() => {
            MainTracker.track('custom', DOCTOR_ALTERNATIVE_EVENTS.DECISION, {
                ...buildOrderBundleProps({
                    hashID: orderBundle?.hashID,
                    groupHashID: orderBundle?.groupHashID,
                    status: orderBundle?.orderStatus,
                    category: orderBundle?.categoryShortcut,
                    price: suggestion?.originalProductPrice,
                }),
                "Decision": decision === "accept" ? "Accept" : "Cancel",
                "Answer method": "My Account",
            });
            if (setStickyNotificationDetails) {
                const translationKey = decision === "accept"
                    ? "stickyNotifications/success/doctor_alternative_accepted"
                    : "stickyNotifications/success/doctor_alternative_denied";
                setStickyNotificationDetails((currentDetails: any) => ({
                    show: true,
                    title: translator.translate(translator.getLanguageByCountry(country), "my_account", "stickyNotifications/success/thank_you"),
                    content: translator.translate(translator.getLanguageByCountry(country), "my_account", translationKey),
                    level: "info",
                    showInfoIcon: false,
                    refresh: !currentDetails.refresh,
                    backgroundColor: decision === "accept" ? undefined : "#185FA5",
                    fontColor: decision === "accept" ? undefined : "#FFFFFF"
                }));
            }
            setTimeout(() => {
                setRefreshData((prev: boolean) => !prev);
                closeModal();
            }, 600);
        }).catch(() => {
            if (setStickyNotificationDetails) {
                setStickyNotificationDetails((currentDetails: any) => ({
                    show: true,
                    title: translator.translate(translator.getLanguageByCountry(country), "my_account", "stickyNotifications/error/errorTitle"),
                    content: translator.translate(translator.getLanguageByCountry(country), "my_account", "stickyNotifications/error/error"),
                    level: "error",
                    showInfoIcon: true,
                    refresh: !currentDetails.refresh
                }));
            }
            setIsSubmitting(false);
        });
    };

    if (!suggestion) {
        return null;
    }

    return (
        <div
            className={`alternative-offer-modal-background visible ${isClosing ? 'closing' : ''}`}
            onClick={closeModalWithAnimation}
        >
            <div
                id="doctor-alternative-offer-modal-container"
                className={`alternative-offer-modal-container ${isClosing ? 'closing' : ''}`}
                onClick={e => e.stopPropagation()}
                style={{
                    transform: `translateY(${currentTranslateY}px)`,
                    transition: modalDragTransition
                }}
            >
                <div className="alternative-offer-modal-header">
                    <button className="out-of-stock-close-button" onClick={closeModalWithAnimation}>
                        <img src={CloseIcon} alt="Close" />
                    </button>

                    <div
                        className="open-close-drag-bar"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                    </div>

                    {/* "Order Update" banner - desktop layout */}
                    <div className="top-attention-sign desktop-only">
                        {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/order_update")}
                    </div>
                </div>

                <div className="alternative-offer-modal-main-content">
                    {/* "Order Update" banner - mobile layout */}
                    <div className="top-attention-sign mobile-only">
                        {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/order_update")}
                    </div>

                    <h1 className="alternative-offer-modal-main-title">
                        {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/doctor_offer_main_title")}
                    </h1>
                    <h2 className="alternative-offer-modal-sub-title">
                        {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/doctor_offer_sub_title")}
                    </h2>

                    {/* product cards + continuation arrow */}
                    <div className="alternative-product-offer-container">
                        <div className="alternative-product-transition-container">
                            <ProductCard
                                cbd={suggestion.originalCbd ?? null}
                                thc={suggestion.originalThc ?? null}
                                strain={suggestion.originalStrain ?? null}
                                imageSrc={suggestion.originalImageSrc}
                                titleView={suggestion.originalProductTitle}
                                price={suggestion.originalProductPrice}
                                catalogPage={null}
                                isOutOfStock={true}
                                badgeKey="not_approved_by_doctor"
                            />

                            <Arrow thickness={1} size={54} />

                            <ProductCard
                                cbd={suggestion.suggestedCbd ?? null}
                                thc={suggestion.suggestedThc ?? null}
                                strain={suggestion.suggestedStrain ?? null}
                                imageSrc={suggestion.suggestedImageSrc}
                                titleView={suggestion.suggestedProductTitle}
                                price={suggestion.suggestedProductPrice}
                                catalogPage={suggestion.suggestedCatalogPage}
                            />
                        </div>
                    </div>

                    {/* disclaimer about pricing */}
                    <div className="out-of-stock-disclaimer">
                        <img
                            className="disclaimer-info-icon"
                            src={require('../../../../src/icons/info-circle-blue.svg').default}
                            alt=""
                        />
                        <span>{translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/doctor_disclaimer")}</span>
                    </div>
                </div>

                <div className="confirmation-section">
                    <button
                        className="accept-offer-button"
                        onClick={() => submitDecision("accept")}
                        disabled={isSubmitting}
                    >
                        {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/doctor_offer_accept_button")}
                    </button>
                    <button
                        className="reject-offer-button"
                        onClick={() => submitDecision("deny")}
                        disabled={isSubmitting}
                    >
                        {translator.translate(translator.getLanguageByCountry(country), "my_account", "MyOrders/details/alternative_offer_modal/doctor_offer_deny_button")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DoctorAlternativeOfferModal;
